import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isTranscribing?: boolean;
  isAudio?: boolean;
}

interface ChatContextValue {
  messages: ChatMessage[];
  isLoading: boolean;
  sendMessage: (content: string) => Promise<void>;
  sendAudioMessage: (blob: Blob) => Promise<void>;
}

export const ChatContext = createContext<ChatContextValue>({
  messages: [],
  isLoading: false,
  sendMessage: async () => undefined,
  sendAudioMessage: async () => undefined,
});

export const useChatContext = () => useContext(ChatContext);

interface ChatProviderProps {
  children: ReactNode;
  url: string;
  audioUrl: string;
  authHeader: string;
  threadId: string;
  onStreamComplete?: () => void;
}

function applyDelta(
  messages: ChatMessage[],
  assistantId: string,
  delta: string,
): ChatMessage[] {
  return messages.map((m) =>
    m.id === assistantId ? { ...m, content: m.content + delta } : m,
  );
}

function applyTranscription(
  messages: ChatMessage[],
  userId: string,
  transcription: string,
): ChatMessage[] {
  return messages.map((m) =>
    m.id === userId
      ? { ...m, content: transcription, isTranscribing: false }
      : m,
  );
}

function buildRequestBody(threadId: string, messages: ChatMessage[]) {
  return {
    method: 'agent/run',
    params: { agentId: 'default' },
    body: {
      threadId,
      runId: crypto.randomUUID(),
      messages,
      tools: [],
      context: [],
      forwardedProps: {},
      state: null,
    },
  };
}

type SseEvent =
  | { kind: 'delta'; value: string }
  | { kind: 'transcription'; value: string };

function parseSseEvent(line: string): SseEvent | null {
  if (!line.startsWith('data: ')) return null;
  const data = line.slice(6).trim();
  if (!data) return null;
  try {
    const event = JSON.parse(data) as {
      type: string;
      delta?: string;
      input?: { messages?: { role: string; content: string }[] };
    };
    if (event.type === 'TEXT_MESSAGE_CONTENT' && event.delta)
      return { kind: 'delta', value: event.delta };
    const transcription = event.input?.messages?.[0]?.content;
    if (event.type === 'RUN_STARTED' && transcription)
      return { kind: 'transcription', value: transcription };
    return null;
  } catch {
    return null;
  }
}

async function readSseStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onDelta: (delta: string) => void,
  onTranscription?: (text: string) => void,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  let done = false;

  while (!done) {
    const chunk = await reader.read();
    done = chunk.done;
    if (done) break;

    buffer += decoder.decode(chunk.value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const event = parseSseEvent(line);
      if (!event) continue;
      if (event.kind === 'delta') onDelta(event.value);
      else if (event.kind === 'transcription') onTranscription?.(event.value);
    }
  }
}

export const ChatProvider = ({
  children,
  url,
  audioUrl,
  authHeader,
  threadId,
  onStreamComplete,
}: ChatProviderProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesRef = useRef<ChatMessage[]>([]);
  messagesRef.current = messages;
  const abortRef = useRef<AbortController | null>(null);
  const onStreamCompleteRef = useRef(onStreamComplete);
  onStreamCompleteRef.current = onStreamComplete;

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const currentMessages = messagesRef.current;
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
      };
      const assistantId = crypto.randomUUID();

      setMessages([
        ...currentMessages,
        userMsg,
        { id: assistantId, role: 'assistant', content: '' },
      ]);
      setIsLoading(true);

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authHeader,
          },
          signal: controller.signal,
          body: JSON.stringify(
            buildRequestBody(threadId, [...currentMessages, userMsg]),
          ),
        });

        if (!response.ok || !response.body)
          throw new Error(`HTTP ${response.status}`);

        await readSseStream(response.body.getReader(), (delta) =>
          setMessages((prev) => applyDelta(prev, assistantId, delta)),
        );
        onStreamCompleteRef.current?.();
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setMessages((prev) => prev.filter((m) => m.id !== assistantId));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [url, authHeader, threadId, isLoading],
  );

  const sendAudioMessage = useCallback(
    async (blob: Blob) => {
      if (isLoading) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const currentMessages = messagesRef.current;
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: '',
        isTranscribing: true,
        isAudio: true,
      };
      const assistantId = crypto.randomUUID();

      setMessages([
        ...currentMessages,
        userMsg,
        { id: assistantId, role: 'assistant', content: '' },
      ]);
      setIsLoading(true);

      try {
        const formData = new FormData();
        formData.append('audio', blob, 'recording.webm');
        formData.append('threadId', threadId);

        const response = await fetch(audioUrl, {
          method: 'POST',
          headers: { Authorization: authHeader },
          signal: controller.signal,
          body: formData,
        });

        if (!response.ok || !response.body)
          throw new Error(`HTTP ${response.status}`);

        await readSseStream(
          response.body.getReader(),
          (delta) =>
            setMessages((prev) => applyDelta(prev, assistantId, delta)),
          (transcription) =>
            setMessages((prev) =>
              applyTranscription(prev, userMsg.id, transcription),
            ),
        );
        onStreamCompleteRef.current?.();
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setMessages((prev) => prev.filter((m) => m.id !== assistantId));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [audioUrl, authHeader, threadId, isLoading],
  );

  const contextValue = useMemo(
    () => ({ messages, isLoading, sendMessage, sendAudioMessage }),
    [messages, isLoading, sendMessage, sendAudioMessage],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};
